// src/services/aeo/delivery.ts
//
// Delivery: the one module screens import for it (types, calls, the change
// broadcast). Staff see each client's deliverables with one next step, and
// the Today list gathers every client's next steps.
//
// Public API                                     live route (handoffs H4 to H29)
//   isDeliveryLive                               VITE_ENABLE_OURA_DELIVERY_LIVE is exactly "true"
//   isDeliveryServed                             live, or the sample store answers
//   getDelivery(businessId, name?)               GET  /businesses/{id}/delivery                          -> DeliveryPayload | null
//   markFixed(businessId, itemId, pages?)        POST /businesses/{id}/delivery/items/{itemId}/mark-fixed  -> Deliverable
//   unmarkFixed(businessId, itemId)              POST .../items/{itemId}/unmark                           -> Deliverable
//   noAction(businessId, itemId, page, reason)   POST .../items/{itemId}/no-action                        -> Deliverable
//   tickItem(businessId, itemId, {link, note})   POST .../items/{itemId}/tick                             -> Deliverable
//   untickItem(businessId, itemId, reason?)      POST .../items/{itemId}/untick                           -> Deliverable
//   closeByHand(businessId, itemId, proof)       POST .../items/{itemId}/close-by-hand (multipart w/ file) -> Deliverable
//   tickPasteStep(b, itemId, stepId, ticked)     POST .../items/{itemId}/paste-list/steps/{stepId}/tick {ticked} -> Deliverable  (H35, D4)
//   copyToSite(businessId, itemId, mode)         POST .../items/{itemId}/copy-to-site {mode}              -> Deliverable  (H39, D8)
//   postLiveSince(b, item, current, {anyway}?)   POST /documents/{docId}/threads/{threadId}/reply (B20, W20), signed by
//                                                the person who clicks; then the delivery is read again    -> Deliverable  (H37, D6)
//   testAiReaders(businessId)                    POST /businesses/{id}/reader-check                       -> ReaderCheck[] (H38, D7),
//                                                then GET the delivery again for the A3 it wrote to       -> Deliverable
//   getReaderChecks(businessId)                  GET  /businesses/{id}/reader-check (client; handoff)     -> ReaderChecksRead
//   addLivePage(businessId, itemId, page)        POST .../items/{itemId}/page                             -> Deliverable
//   setSlot(businessId, itemId, slot)            PUT  .../items/{itemId}/slot                             -> Deliverable
//   reopenDeliverable(businessId, key, reason)   POST .../deliverables/{key}/reopen                       -> Deliverable
//   forceItemDone(businessId, itemId)            POST .../items/{itemId}/force-done (#6463)               -> Deliverable
//   forceDeliverableDone(businessId, key)        POST .../deliverables/{key}/force-done (#6463)           -> Deliverable
//   checkNow(businessId)                         POST /businesses/{id}/delivery/check-now                 -> {startedAt, etaSeconds}
//   getKeyPages(businessId)                      GET  /businesses/{id}/key-pages                          -> KeyPages | null
//   putKeyPages(businessId, urls)                PUT  /businesses/{id}/key-pages {urls}                   -> KeyPages
//   getSuggestions(businessId)                   GET  /businesses/{id}/suggestions                        -> SuggestionsPayload
//   decideDeliverySuggestion(b, id, decision)    POST /businesses/{id}/suggestions/{sid}/keep|dismiss     -> SuggestionsPayload
//   getLedger(businessId)                        GET  /businesses/{id}/delivery/ledger                    -> {counts, rows}
//   getNoteDraft(businessId, name?, lastNoteAt?) GET  /businesses/{id}/client-note/draft                  -> NoteDraft
//   getToday()                                   GET  /delivery/today                                     -> TodayPayload
//   subscribeDelivery(cb)                        told after any write, so open screens reload
//
// Every call resolves and never throws: {ok: true, data} or a DeliveryFailure
// whose `reason` says which (network, unauthorised, forbidden, notFound,
// notServed, rejected). With the switch unset, a local run serves the sample
// store (deliveryMock.ts); staging and production answer notServed, and their
// staff keep Fixes and Plan until the routes ship.

import axios, { type AxiosResponse } from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { readableDeliverable } from "./lineWords";
import type {
    CheckNowResult,
    CopyToSiteMode,
    Deliverable,
    DeliveryFailure,
    DeliveryItem,
    DeliveryPayload,
    DeliveryResult,
    KeyPages,
    LedgerCounts,
    LiveSinceDraft,
    LedgerRow,
    NoteDraft,
    PageIn,
    ProofIn,
    ReaderCheck,
    SlotIn,
    SuggestionsPayload,
    TickIn,
    TodayPayload,
} from "./deliveryTypes";
import { deliveryFailure } from "./deliveryTypes";
import { noteDraftOf, readerItemOf } from "./deliveryRules";
import { myDate } from "./clientDocsRules";
import { DELIVERY_SAMPLE_SERVES, deliveryMock, setDeliveryMockListener } from "./deliveryMock";
import { getEngagement, type Engagement } from "./clientReview";
import { getBusiness, listBusinesses } from "../../oura/data";
import { DOCS_WRITE_ON } from "../../oura/docs/writeFlag";
import { replyInThread } from "./clientDocsWrite";

export * from "./deliveryTypes";
export { actorText, GROUP_TITLE, GROUP_ORDER, KIND_LABEL, dayLabel, shortDay, workingDayText, KEY_PAGE_CODES, todayRank, READERS_PART, UNTESTED_READERS, isReadersItem, readerItemOf } from "./deliveryRules";

export const isDeliveryLive = import.meta.env.VITE_ENABLE_OURA_DELIVERY_LIVE === "true";

/** Whether Delivery answers here at all: live, or the sample store on a fixtures run. */
export const isDeliveryServed = isDeliveryLive || DELIVERY_SAMPLE_SERVES;

function sample<T>(run: () => Promise<DeliveryResult<T>>): Promise<DeliveryResult<T>> {
    return DELIVERY_SAMPLE_SERVES ? run() : Promise.resolve(deliveryFailure("notServed"));
}

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
const enc = encodeURIComponent;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

const opts = () => ({ headers: authHeaders(), timeout: 30_000 });

/* ------------------------------------------------------------------------- *
 * Change broadcast
 * ------------------------------------------------------------------------- */

const listeners = new Set<() => void>();

export function subscribeDelivery(cb: () => void): () => void {
    listeners.add(cb);
    return () => {
        listeners.delete(cb);
    };
}

function emitDeliveryChanged(): void {
    Array.from(listeners).forEach((cb) => cb());
}

/** Something outside Delivery changed what it reads (a client was just signed): open screens read it again. */
export function announceDeliveryChange(): void {
    emitDeliveryChanged();
}

setDeliveryMockListener(emitDeliveryChanged);

function thenEmit<T>(p: Promise<DeliveryResult<T>>): Promise<DeliveryResult<T>> {
    return p.then((r) => {
        if (!r.ok) return r;
        emitDeliveryChanged();
        // A line sent back after a write reads the same as on the list.
        const d = r.data as unknown;
        return d && typeof d === "object" && "label" in d && "items" in d ? { ...r, data: readableDeliverable(d as Deliverable) as T } : r;
    });
}

/* ------------------------------------------------------------------------- *
 * Live calls
 * ------------------------------------------------------------------------- */

export function readDeliveryError(err: unknown, what: string): DeliveryFailure {
    const e = err as { response?: { status?: number; data?: unknown } };
    const res = e.response;
    if (!res) {
        catchErrorLog(`[oura] delivery ${what}: no response`);
        return deliveryFailure("network");
    }
    const status = res.status ?? null;
    const body = (res.data && typeof res.data === "object" ? res.data : {}) as { code?: unknown; message?: unknown; errors?: unknown };
    // The refusal slug travels in errors[0]; `code` is the HTTP status as a number (StandardResponse).
    const first: unknown = Array.isArray(body.errors) ? body.errors[0] : null;
    const slug = typeof first === "string" ? first : first && typeof first === "object" ? (first as { code?: unknown }).code : null;
    const code = typeof slug === "string" && slug ? slug : typeof body.code === "string" ? body.code : null;
    const message = typeof body.message === "string" && body.message.trim() ? body.message : null;
    if (status === 401) return deliveryFailure("unauthorised", { status });
    if (status === 403) return deliveryFailure("forbidden", { status });
    if (status === 404) return "message" in body || "code" in body ? deliveryFailure("notFound", { status, message }) : deliveryFailure("notServed", { status });
    if (status === 405 || status === 501) return deliveryFailure("notServed", { status });
    if (status === 409 || status === 422 || status === 400) return deliveryFailure("rejected", { status, code, message });
    catchErrorLog(`[oura] delivery ${what} failed (${status ?? "no status"})`);
    return deliveryFailure("network", { status, message });
}

async function call<T>(what: string, run: () => Promise<AxiosResponse>): Promise<DeliveryResult<T>> {
    try {
        const res = await run();
        return { ok: true, data: (res.data as { data: T }).data };
    } catch (err) {
        return readDeliveryError(err, what);
    }
}

const itemBase = (b: string, itemId: string) => `${AEO_BASE}/businesses/${enc(b)}/delivery/items/${enc(itemId)}`;

async function sampleEngagement(businessId: string): Promise<Engagement | null> {
    try {
        return (await getEngagement(businessId)) ?? null;
    } catch {
        return null;
    }
}

/* ------------------------------------------------------------------------- *
 * The calls
 * ------------------------------------------------------------------------- */

/** One client's Delivery, or null when the client has no contract in Oura yet. */
export function getDelivery(businessId: string, businessName?: string): Promise<DeliveryResult<DeliveryPayload | null>> {
    if (!isDeliveryLive) {
        return sample(async () => {
            const e = await sampleEngagement(businessId);
            const name = businessName ?? getBusiness(businessId)?.name ?? e?.client ?? businessId;
            return deliveryMock.getDelivery(businessId, name, e);
        });
    }
    return call<DeliveryPayload | null>("read", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/delivery`, opts())).then((r) =>
        r.ok && r.data
            ? { ok: true as const, data: { ...r.data, deliverables: r.data.deliverables.map((d) => readableDeliverable(DOCS_WRITE_ON ? d : withoutDocWrites(d))) } }
            : r,
    );
}

/**
 * Fail closed (v1.3): with the Docs write switch off, a server's `create_doc`
 * reads as the Phase 1 action it replaces, so a build missing the switch
 * offers no write: "Open the doc" for a covering doc, else "Open Client".
 */
function withoutDocWrites(d: Deliverable): Deliverable {
    const a = d.mainAction;
    if (a.kind !== "create_doc") return d;
    return {
        ...d,
        mainAction: a.docId ? { kind: "open_doc", label: "Open the doc", docId: a.docId, itemId: a.itemId ?? null } : { kind: "go_to", label: "Open Client", to: "reviews" },
    };
}

/** Mark a check fixed on its pages (all of them by default). It waits for Oura's re-check. */
export function markFixed(businessId: string, itemId: string, pages?: string[]): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.markFixed(businessId, itemId, pages)));
    return thenEmit(call("mark fixed", () => axios.post(`${itemBase(businessId, itemId)}/mark-fixed`, pages ? { pages } : {}, opts())));
}

/** Undo a Mark fixed, before Oura's re-check reads it. */
export function unmarkFixed(businessId: string, itemId: string): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.unmarkFixed(businessId, itemId)));
    return thenEmit(call("unmark", () => axios.post(`${itemBase(businessId, itemId)}/unmark`, {}, opts())));
}

/** Close one page of a check without fixing it, with a one-line reason. */
export function noAction(businessId: string, itemId: string, page: string, reason: string): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.noAction(businessId, itemId, page, reason)));
    return thenEmit(call("no action", () => axios.post(`${itemBase(businessId, itemId)}/no-action`, { page, reason }, opts())));
}

/** Tick a We confirm it item, with a link or a one-line note. */
export function tickItem(businessId: string, itemId: string, body: TickIn): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.tick(businessId, itemId, body)));
    return thenEmit(call("tick", () => axios.post(`${itemBase(businessId, itemId)}/tick`, body, opts())));
}

export function untickItem(businessId: string, itemId: string, reason?: string): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.untick(businessId, itemId, reason)));
    return thenEmit(call("untick", () => axios.post(`${itemBase(businessId, itemId)}/untick`, reason ? { reason } : {}, opts())));
}

/**
 * When Oura cannot read a page, staff can close its item with proof. It shows
 * as "Closed by hand". H36 (v1.3): a screenshot or a PDF can go with it, sent
 * as a multipart body; only behind the Docs write switch.
 */
export function closeByHand(businessId: string, itemId: string, body: { link?: string; note: string; file?: File | null }): Promise<DeliveryResult<Deliverable>> {
    const file = DOCS_WRITE_ON ? body.file ?? null : null;
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.closeByHand(businessId, itemId, { link: body.link, note: body.note, file })));
    if (!file) return thenEmit(call("close by hand", () => axios.post(`${itemBase(businessId, itemId)}/close-by-hand`, { link: body.link, note: body.note }, opts())));
    const form = new FormData();
    form.append("note", body.note);
    if (body.link) form.append("link", body.link);
    form.append("file", file);
    return thenEmit(call("close by hand", () => axios.post(`${itemBase(businessId, itemId)}/close-by-hand`, form, opts())));
}

/* ------------------------------------------------------------------------- *
 * Phase 3 on items (v1.3, H35, H38, H39). Each answers notServed without the
 * Docs write switch, so a build missing it can never reach these routes.
 * ------------------------------------------------------------------------- */

const workOff = () => Promise.resolve(deliveryFailure("notServed"));

/**
 * Tick (or untick) one step of an item's paste list (H35). Ticking the last
 * step marks the item fixed, so Oura's same-day re-check reads it.
 */
export function tickPasteStep(businessId: string, itemId: string, stepId: string, ticked: boolean): Promise<DeliveryResult<Deliverable>> {
    if (!DOCS_WRITE_ON) return workOff();
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.tickPasteStep(businessId, itemId, stepId, ticked)));
    return thenEmit(call("paste step", () => axios.post(`${itemBase(businessId, itemId)}/paste-list/steps/${enc(stepId)}/tick`, { ticked }, opts())));
}

/**
 * Approved copy to the site (H39, D8): `draft_page` makes an unpublished
 * WordPress draft page where the connection allows; `download` makes the
 * approved copy ready to download where Oura cannot create a page (Wix,
 * Framer). A named person publishes or pastes it on the site; Oura never does.
 */
export function copyToSite(businessId: string, itemId: string, mode: CopyToSiteMode): Promise<DeliveryResult<Deliverable>> {
    if (!DOCS_WRITE_ON) return workOff();
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.copyToSite(businessId, itemId, mode)));
    return thenEmit(call("copy to site", () => axios.post(`${itemBase(businessId, itemId)}/copy-to-site`, { mode }, opts())));
}

/**
 * Post the drafted "Live since" reply in the doc (H37, D6), as the person who
 * clicks: Oura's staff reply route (B20) posts it from oura@, signed with
 * their name. Oura never posts it on its own. The delivery is read again for
 * the deliverable, which now records who posted it and when.
 *
 * Once the reply has gone, this never reports a failure. The post and the
 * read are two calls: if the read after it fails, or comes back before the
 * server has marked the draft posted, a failure would put "Post it in the
 * doc" back on screen and a second click would post the same reply twice in
 * the client's doc. So the item is marked posted here instead (postedAt now,
 * on `current`, the deliverable as the screen holds it), the result carries a
 * `note` saying the screen catches up on its next read, and the draft is
 * remembered for this tab (`liveSincePostedHere`) so a reload that still
 * reads it unposted does not offer it again.
 *
 * When the reply's own answer is lost (no HTTP status, a 5xx with no code of
 * the server's, or a response that would not read), nobody knows whether the
 * reply went into the doc. That is never offered as safe to retry: the draft
 * is marked "may have posted" for this tab (`liveSinceMayHavePosted`) and the
 * result is refused `may_have_posted`, so the block asks the person to check
 * the thread and offers only "Post it anyway". A refusal the server gave with
 * its own code (a 4xx) did not post, and stays an ordinary error. A later read
 * that shows `postedAt` ends the doubt; so does a post that goes through.
 *
 * Every post sends a stable `Idempotency-Key` (business, item, doc, thread,
 * draft time). A retry from another tab or device, or "Post it anyway" after a
 * lost answer, only becomes safe once the reply route honours that key and
 * returns the first post for a repeated one (backend).
 */
export async function postLiveSince(
    businessId: string,
    item: Pick<DeliveryItem, "id" | "liveSinceDraft">,
    current: Deliverable,
    options: { anyway?: boolean } = {},
): Promise<{ ok: true; data: Deliverable; note?: string } | DeliveryFailure> {
    if (!DOCS_WRITE_ON) return workOff();
    const r = item.liveSinceDraft;
    if (!r || r.postedAt || liveSincePostedHere(businessId, item.id, r)) return deliveryFailure("rejected", { code: "already_posted", message: "This reply is already posted." });
    const key = liveSinceKey(businessId, item.id, r);
    if (mayHavePosted.has(key) && !options.anyway) return deliveryFailure("rejected", { code: MAY_HAVE_POSTED, message: MAY_HAVE_POSTED_TEXT });
    let posted: Awaited<ReturnType<typeof replyInThread>> | null;
    try {
        posted = await replyInThread(r.docId, r.threadId, r.text, { idempotencyKey: key });
    } catch {
        posted = null;
    }
    if (!posted || (!posted.ok && posted.reason === "network" && (posted.status === null || posted.status >= 500))) {
        // The exact words of the attempt stay with the doubt: a retry under the same key must send the same reply.
        mayHavePosted.set(key, r.text);
        return deliveryFailure("rejected", { code: MAY_HAVE_POSTED, message: MAY_HAVE_POSTED_TEXT });
    }
    if (!posted.ok) return deliveryFailure(posted.reason === "notServed" ? "notServed" : "rejected", { code: posted.code, message: posted.message });
    const at = new Date().toISOString();
    mayHavePosted.delete(key);
    postedHere.set(key, at);

    let fresh: Deliverable | null = null;
    try {
        if (!isDeliveryLive) {
            const read = await sample(() => deliveryMock.liveSincePosted(businessId, item.id));
            fresh = read.ok ? read.data : null;
        } else {
            const read = await getDelivery(businessId);
            fresh = (read.ok && read.data?.deliverables.find((x) => x.items.some((i) => i.id === item.id))) || null;
        }
    } catch {
        fresh = null;
    }
    const freshItem = fresh?.items.find((i) => i.id === item.id);
    emitDeliveryChanged();
    if (fresh && freshItem?.liveSinceDraft?.postedAt) return { ok: true, data: fresh };
    // Posted, but the read did not show it: the screen shows it posted now, and catches up on its next read.
    const base = fresh ?? current;
    const data: Deliverable = {
        ...base,
        items: base.items.map((i) => (i.id === item.id && i.liveSinceDraft ? { ...i, liveSinceDraft: { ...i.liveSinceDraft, text: r.text, postedAt: at } } : i)),
    };
    return { ok: true, data, note: "Live since reply posted in the doc. This screen shows who posted it on its next read." };
}

/** The refusal code for a Live since reply whose answer was lost: it may be in the doc already. */
export const MAY_HAVE_POSTED = "may_have_posted";
const MAY_HAVE_POSTED_TEXT = "The reply may have posted. Open the doc and check the thread before posting again.";

/** Live since replies posted from this tab, by item and draft: never offered again here, whatever a read says. */
const postedHere = new Map<string, string>();
/** Live since replies whose post lost its answer in this tab, with the words that were sent: offered again only as "Post it anyway", with those words. */
const mayHavePosted = new Map<string, string>();
/** One draft's key, also its Idempotency-Key: header-safe, and the same on every retry of that draft. */
const liveSinceKey = (businessId: string, itemId: string, r: Pick<LiveSinceDraft, "docId" | "threadId" | "madeAt">) =>
    ["delivery-live-since", businessId, itemId, r.docId, r.threadId, r.madeAt].map(encodeURIComponent).join(":");

/** When this tab posted the draft's reply, or null: the Live since block reads it as posted until the server says so. */
export function liveSincePostedHere(businessId: string, itemId: string, r: Pick<LiveSinceDraft, "docId" | "threadId" | "madeAt">): string | null {
    return postedHere.get(liveSinceKey(businessId, itemId, r)) ?? null;
}

/**
 * The words of this draft's last post when its answer was lost in this tab, or
 * null. While in doubt the block shows and retries exactly these words: the
 * Idempotency-Key names the draft, so a retry with other words would either get
 * the first post back (and show the wrong words as posted) or be refused.
 */
export function liveSinceMayHavePostedText(businessId: string, itemId: string, r: Pick<LiveSinceDraft, "docId" | "threadId" | "madeAt" | "postedAt">): string | null {
    const key = liveSinceKey(businessId, itemId, r);
    // A read that shows it posted ends the doubt for good.
    if (r.postedAt) mayHavePosted.delete(key);
    return mayHavePosted.get(key) ?? null;
}

/** Whether this draft's last post lost its answer in this tab, and no read has shown it posted since. */
export function liveSinceMayHavePosted(businessId: string, itemId: string, r: Pick<LiveSinceDraft, "docId" | "threadId" | "madeAt" | "postedAt">): boolean {
    return liveSinceMayHavePostedText(businessId, itemId, r) !== null;
}

/**
 * The AI reader test (H38, D7): robots.txt, a fetch as each reader, and
 * whether the words show without scripts. All 3 in closes A3's AI readers
 * item on that proof. The route answers the checks; the delivery is read
 * again for the A3 it wrote to, so every screen applies the same deliverable.
 */
export async function testAiReaders(businessId: string): Promise<DeliveryResult<Deliverable>> {
    if (!DOCS_WRITE_ON) return workOff();
    const ran = isDeliveryLive
        ? await call<ReaderCheck[]>("reader test", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/reader-check`, {}, opts()))
        : await sample(() => deliveryMock.testAiReaders(businessId));
    if (!ran.ok) return ran;
    const read = await getDelivery(businessId);
    if (!read.ok) return read;
    const hit = read.data ? readerItemOf(read.data.deliverables, myDate(new Date())) : null;
    return thenEmit(Promise.resolve(hit ? { ok: true as const, data: hit.d } : deliveryFailure("notFound")));
}

/**
 * The client's own Website page reads the AI reader lines here.
 *
 * The lines live on the delivery, which is staff-only (a client token is
 * refused), so the client reads the latest stored test from a GET beside the
 * staff POST: handoff "Client-readable AI reader checks" (14 Oct 2026). Until
 * that route ships the switch stays off and this reads "none", so the page
 * shows no readers card rather than calling a route that is not there. On
 * sample data it reads the sample delivery's A3 item, as staff do.
 *
 * A failed read is "error", never "none": an outage must not look like a
 * business with no result yet (Jack's review of #4210).
 */
export const isClientReadersLive: boolean = import.meta.env.VITE_ENABLE_OURA_CLIENT_READERS_LIVE === "true";

export type ReaderChecksRead = { kind: "ready"; checks: ReaderCheck[] } | { kind: "none" } | { kind: "error" };

export async function getReaderChecks(businessId: string): Promise<ReaderChecksRead> {
    if (!isDeliveryLive) {
        const read = await getDelivery(businessId);
        if (!read.ok) return { kind: "error" };
        const lines = read.data ? readerItemOf(read.data.deliverables, myDate(new Date()))?.item.readerChecks ?? null : null;
        return lines?.length ? { kind: "ready", checks: lines } : { kind: "none" };
    }
    if (!isClientReadersLive) return { kind: "none" };
    const read = await call<ReaderCheck[]>("reader lines", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/reader-check`, opts()));
    if (!read.ok) {
        // readDeliveryError logs the network and server failures itself; the
        // refusals it maps (401, 403, 404) it does not, and here each is an outage.
        if (read.reason !== "network") catchErrorLog(`[oura] client reader lines for ${businessId}: ${read.reason} (${read.status ?? "no status"})`);
        return { kind: "error" };
    }
    return read.data?.length ? { kind: "ready", checks: read.data } : { kind: "none" };
}

/** Fill a page slot: a new page's live address, or an existing page and what changed. Oura reads it on its next check. */
export function addLivePage(businessId: string, itemId: string, body: PageIn): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.setPage(businessId, itemId, body)));
    return thenEmit(call("add live page", () => axios.post(`${itemBase(businessId, itemId)}/page`, body, opts())));
}

/** Agree a slot's topic, or whether it is a new page or an existing one. */
export function setSlot(businessId: string, itemId: string, body: SlotIn): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.setSlot(businessId, itemId, body)));
    return thenEmit(call("set slot", () => axios.put(`${itemBase(businessId, itemId)}/slot`, body, opts())));
}

export function reopenDeliverable(businessId: string, key: string, reason: string): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.reopen(businessId, key, reason)));
    return thenEmit(call("reopen", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/delivery/deliverables/${enc(key)}/reopen`, { reason }, opts())));
}

/** Mark one item Done by hand (#6463): one click, nothing to type. Undo is `untickItem`. */
export function forceItemDone(businessId: string, itemId: string): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.forceItemDone(businessId, itemId)));
    return thenEmit(call("mark done", () => axios.post(`${itemBase(businessId, itemId)}/force-done`, {}, opts())));
}

/** Mark a whole deliverable Done by hand (#6463), every item with it. Undo is `reopenDeliverable`. */
export function forceDeliverableDone(businessId: string, key: string): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.forceDeliverableDone(businessId, key)));
    return thenEmit(call("mark done", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/delivery/deliverables/${enc(key)}/force-done`, {}, opts())));
}

/** A proof file goes with "Add what we did" only where the upload is sent: the picker and the request share this gate. */
export const DELIVERY_PROOF_FILE_ON = DOCS_WRITE_ON;

/**
 * A deliverable whose check passed with no work logged (`passedWithoutWork`):
 * staff add what we did, or mark it already fine (Jack, 22 Oct 2026). POST
 * …/delivery/deliverables/{key}/proof; a file goes multipart, as Close by hand.
 */
export function confirmProof(businessId: string, key: string, body: ProofIn): Promise<DeliveryResult<Deliverable>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.proof(businessId, key, body)));
    const url = `${AEO_BASE}/businesses/${enc(businessId)}/delivery/deliverables/${enc(key)}/proof`;
    const file = body.kind === "work" && DELIVERY_PROOF_FILE_ON ? body.file ?? null : null;
    if (body.kind === "work" && file) {
        const form = new FormData();
        form.append("kind", "work");
        form.append("note", body.note);
        if (body.link) form.append("link", body.link);
        form.append("file", file);
        return thenEmit(call("proof", () => axios.post(url, form, opts())));
    }
    const json = body.kind === "work" ? { kind: "work", note: body.note, link: body.link ?? null } : { kind: "already_fine", note: body.note ?? null };
    return thenEmit(call("proof", () => axios.post(url, json, opts())));
}

/** Oura's re-check now, without AI spend: about a minute for a 600-page site. */
export function checkNow(businessId: string): Promise<DeliveryResult<CheckNowResult>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.checkNow(businessId)));
    return thenEmit(call("check now", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/delivery/check-now`, {}, opts())));
}

export function getKeyPages(businessId: string): Promise<DeliveryResult<KeyPages | null>> {
    if (!isDeliveryLive) return sample(() => deliveryMock.getKeyPages(businessId));
    return call("key pages", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/key-pages`, opts()));
}

/** Exactly 10 addresses. Applies from the next check. */
export function putKeyPages(businessId: string, urls: string[]): Promise<DeliveryResult<KeyPages>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.putKeyPages(businessId, urls)));
    return thenEmit(call("key pages", () => axios.put(`${AEO_BASE}/businesses/${enc(businessId)}/key-pages`, { urls }, opts())));
}

/**
 * The Friday note, drafted from this week's work (H17). Live, the server's
 * draft; on the sample, the same template over the sample's Delivery.
 */
export function getNoteDraft(businessId: string, businessName?: string, lastNoteAt: string | null = null): Promise<DeliveryResult<NoteDraft>> {
    if (!isDeliveryLive) {
        return sample(async () => {
            const r = await getDelivery(businessId, businessName);
            if (!r.ok) return r;
            if (!r.data) return deliveryFailure("notFound", { message: "No contract in Oura yet, so there is no week to draft from." });
            return { ok: true, data: noteDraftOf(r.data, myDate(new Date()), lastNoteAt) };
        });
    }
    return call("note draft", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/client-note/draft`, opts()));
}

/** The last scan's suggestions and every decision (H11, section 6). */
export function getSuggestions(businessId: string): Promise<DeliveryResult<SuggestionsPayload>> {
    if (!isDeliveryLive) return sample(() => deliveryMock.getSuggestions(businessId));
    return call("suggestions", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/suggestions`, opts()));
}

/** Keep (it becomes work under its deliverable) or dismiss (Oura stops suggesting it). */
export function decideDeliverySuggestion(businessId: string, suggestionId: string, decision: "keep" | "dismiss"): Promise<DeliveryResult<SuggestionsPayload>> {
    if (!isDeliveryLive) return thenEmit(sample(() => deliveryMock.decideSuggestion(businessId, suggestionId, decision)));
    return thenEmit(call("suggestion", () => axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/suggestions/${enc(suggestionId)}/${decision}`, {}, opts())));
}

export function getLedger(businessId: string): Promise<DeliveryResult<{ counts: LedgerCounts; rows: LedgerRow[] }>> {
    if (!isDeliveryLive) return sample(() => deliveryMock.getLedger(businessId));
    return call("ledger", () => axios.get(`${AEO_BASE}/businesses/${enc(businessId)}/delivery/ledger`, opts()));
}

/** Every client's next step that needs us, sorted by the next-step rule. */
export function getToday(): Promise<DeliveryResult<TodayPayload>> {
    if (!isDeliveryLive) {
        return sample(async () => {
            const clients = await Promise.all(
                listBusinesses().map(async (b) => ({ businessId: b.id, businessName: b.name, engagement: await sampleEngagement(b.id) })),
            );
            return deliveryMock.getToday(clients);
        });
    }
    return call("today", () => axios.get(`${AEO_BASE}/delivery/today`, opts()));
}
